<!--
// app/pages/[region]/players-variable.vue (11 & 12 & 13)
-->
<script setup lang="ts">
import type { Player } from "#shared/player/domain/Player";
import type { SeasonStatsPartner } from "#shared/season-cluster/domain/models/01.Season";
import type { BannerCard } from "#shared/content/domain/models/Banner";
import HeaderBannerTitle from "~/components/Shared/HeaderBannerTitle.vue";
import SeasonsFilter from "~/components/Shared/SeasonsFilter.vue";
import SearchField from "~/components/Shared/SearchField.vue";
import CardStoreAd from "~/components/Shared/CardStoreAd.vue";
import BaseBackdropLoading from "~/components/base/BaseBackdropLoading.vue";
import CardNoDataMessage from "~/components/Shared/CardNoDataMessage.vue";
import CardPlayerFeaturedCategory from "~/components/Shared/players/CardPlayerFeaturedCategory.vue";
import PaginationNav from "~/components/Shared/PaginationNav.vue";
import ArrowFilterIcon from "~/components/Shared/icons/ArrowFilterIcon.vue";
import TeamsFilter from "~/components/Shared/TeamsFilter.vue";
import { $LEGEND_PLAYER_IDS } from "#shared/player/domain/services/playerService";
import { useSeasons } from "~/composables/season/seasons";
import { useSeasonFilter } from "~/composables/season/seasonFilter";
import { useWebContext } from "~/composables/webContext";
import { usePagination } from "~/composables/pages/pagination";
import bannerUiService from "#shared/content/infrastructure/ui-services/bannerUiService";
import { slugify } from "#shared/Shared/utils";
import { useCompetitionStore } from "~/stores/competitionStore";
import { $SEASONS_WITHOUT_DATA } from "#shared/season-cluster/infrastructure/ui-services/seasonUiService";

definePageMeta({
  i18n: {
    paths: {
      en: "/[region]/wildcard-players",
      es: "/[region]/jugadores-wildcard",
      de: "/[region]/wildcard-spieler",
      fr: "/[region]/joueurs-wildcard",
      it: "/[region]/giocatori-wildcard",
      pt: "/[region]/jogadores-wildcard",
    },
  },
  middleware: ["not-found-king-of-kings-nations", "not-found-mena"],
});

const { t, locale } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const { contextSiteTitle, contextDomain } = useWebContext();
const { getCardBanner } = bannerUiService();
const { getSeasonVariablePlayers } = useSeasons();
const { currentSeason, region } = useCompetitionStore();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { currentPage, itemsPerPage, resetPage } = usePagination();

const storeBanner = ref<BannerCard | undefined>();
const seasonPlayers = ref<Player[]>();
const seasonMvpPartner = ref<SeasonStatsPartner>();
const loadingSeasonData = ref<boolean>(false);
const errorMessage = ref<boolean>(false);
const playersWrapperKey = ref<number>(0);
const searchInputValue = ref<string | null>(null);
const teamsOptions = ref<string[] | undefined>();
const turnsOptions = ref<string[] | undefined>();
const isMobile = ref<boolean>(false);
const showFilterItems = ref<boolean>(false);

const filters = reactive({
  team: 0,
  turn: 0,
});

onBeforeMount(() => {
  isMobile.value = window.innerWidth < 1025;
  window.addEventListener("resize", () => {
    isMobile.value = window.innerWidth < 1025;
  });
});

onMounted(async () => {
  await nextTick();
  storeBanner.value = await getCardBanner("players", locale.value);
});

const getSeasonData = async (): Promise<void> => {
  try {
    loadingSeasonData.value = true;
    errorMessage.value = false;
    filters.team = 0;
    filters.turn = 0;

    const seasonIdValue = seasonId.value;
    let fetchedPlayers: Player[] | undefined;

    if (seasonIdValue) {
      fetchedPlayers = await getSeasonVariablePlayers(seasonIdValue);

      // define team options
      const _teamsOptions = [];
      fetchedPlayers?.forEach(p => {
        if (p.teams) {
          const team = p.teams[0].completeName;
          const teamExists = _teamsOptions.some(t => t === team);
          if (!teamExists) _teamsOptions.push(team);
        }
      });

      // define turn options
      const allTurns = [];
      fetchedPlayers?.forEach(p => {
        if (p.turns) allTurns.push(...p.turns);
      });

      turnsOptions.value = allTurns.length ? [...new Set(allTurns)] : undefined;
      seasonPlayers.value = fetchedPlayers;
      teamsOptions.value = _teamsOptions.length
        ? _teamsOptions.sort((a, b) => a.localeCompare(b))
        : undefined;

      seasonMvpPartner.value =
        currentSeason && currentSeason.id === seasonIdValue
          ? currentSeason.metaInformation?.stats_partner
          : undefined;
    }
  } catch (e) {
    errorMessage.value = true;
  } finally {
    loadingSeasonData.value = false;
    playersWrapperKey.value++;
  }
};

await getSeasonData();

const handleSeasonChange = async (): Promise<void> => {
  await getSeasonData();
  resetPage();
};

const filteredPlayers = computed((): Player[] | undefined => {
  if (!seasonPlayers.value) return [];

  let _players = seasonPlayers.value;

  if (_players?.length) {
    if (searchInputValue.value) {
      const cleanValue = searchInputValue.value.toLowerCase();
      _players = _players.filter(p => p.shortName.toLowerCase().includes(cleanValue));
    }

    if (filters.team !== 0) {
      _players = _players.filter(p => {
        return p.teams ? p.teams[0].completeName === filters.team : false;
      });
    }

    if (filters.turn !== 0) {
      _players = _players.filter(p => p.turns?.length && p.turns.includes(filters.turn));
    }
  }

  return _players;
});

const paginatedPlayers = computed((): Player[] | undefined => {
  const startIndex = (currentPage.value - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return filteredPlayers.value?.slice(startIndex, endIndex);
});

const toggleShowFilters = () => {
  if (isMobile.value) {
    showFilterItems.value = !showFilterItems.value;
  }
};

function resetFilters() {
  filters.team = 0;
  filters.turn = 0;
  searchInputValue.value = null;
}

const checkIfIsPLayerLegend = (playerId: number): boolean => {
  const legendsIds = $LEGEND_PLAYER_IDS || [];
  return legendsIds.includes(playerId);
};

const playersSchema = computed(() => {
  if (!paginatedPlayers.value) return [];

  return paginatedPlayers.value?.map((p, index) => {
    const url = localePath({
      name: "players-playerId-playerName",
      params: {
        playerId: p.id,
        playerName: slugify(p.shortName),
      },
    });
    return `{"@type": "ListItem","position": "${index + 1}","item": {"@type" : "Person","name" : "${p.shortName}","url":"https://${contextDomain + url}"}}`;
  });
});

useSeoMeta({
  title: t("variablePlayers.banner.title"),
  ogTitle: t("variablePlayers.banner.title"),
  description: t("meta.description.variablePlayers.page", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.variablePlayers.page", {
    league: region.leagueTitle || contextSiteTitle,
  }),
});

useHead({
  script: () => [
    {
      type: "application/ld+json",
      innerHTML: `{"@context": "https://schema.org","@type": "ItemList", "itemListElement": [${playersSchema.value}]}`,
    },
  ],
});
</script>

<template>
  <header-banner-title :title="$t('variablePlayers.banner.title')" />

  <section class="pb-4 md:py-8 sm:px-4">
    <div class="kql-w-container">
      <div class="px-4 sm:px-0 md:hidden">
        <seasons-filter
          v-model:season-cluster-id="seasonClusterId"
          v-model:season-id="seasonId"
          @change="handleSeasonChange"
        />
      </div>

      <div class="p-4 md:p-0 border-y border-gray-800 md:border-none">
        <div v-if="isMobile" class="flex items-center justify-between">
          <p v-if="filteredPlayers" class="text-gray-600 text-sm">{{ filteredPlayers.length }}</p>
          <div v-if="!!searchInputValue || filters.turn !== 0 || filters.team !== 0" class="min-w-max ml-2">
            <button class="text-primary px-3 py-2 text-xs flex items-center" @click="resetFilters">
              <svg
                class="w-2.5 h-2.5 mr-1"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              {{ $t("draft.player.filters.clean") }}
            </button>
          </div>
          <button
            class="text-gray-600 text-sm rounded-lg w-full flex justify-end items-center py-2"
            @click="toggleShowFilters"
          >
            {{ $t("shared.filter.title") }}
            <ArrowFilterIcon :show-filter-items="showFilterItems" />
          </button>
        </div>

        <div
          v-show="showFilterItems || !isMobile"
          class="flex flex-col md:flex-row flex-wrap items-stretch justify-center md:items-center md:justify-end gap-x-4 md:px-6"
        >
          <div class="hidden md:block">
            <seasons-filter
              v-model:season-cluster-id="seasonClusterId"
              v-model:season-id="seasonId"
              @change="handleSeasonChange"
            />
          </div>
          <div
            class="flex flex-col md:flex-row items-center justify-between min-w-24 gap-x-4 mb-4 mt-2 md:my-0"
          >
            <TeamsFilter
              v-if="teamsOptions && teamsOptions.length"
              v-model="filters.team"
              :teams-options="teamsOptions"
              @change="resetPage"
            />

            <select
              v-if="turnsOptions && turnsOptions.length && !$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
              v-model="filters.turn"
              :class="[
                'kql-select text-xs lg:text-sm border-l rounded-lg w-full md:max-w-44 mt-4 md:mt-0',
                { '!bg-primary !text-black': filters.turn !== 0 },
              ]"
              @change="resetPage"
            >
              <option :value="0">
                {{ filters.turn === 0 ? $t("general.matchday.word") : $t("general.all.female") }}
              </option>
              <option v-for="turn in turnsOptions" :value="turn">{{ turn }}</option>
            </select>
          </div>
          <div class="flex-shrink w-full md:w-auto flex items-center justify-center">
            <search-field v-model.trim="searchInputValue" @change="resetPage" />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-[336px_1fr_1fr] gap-4 px-0 lg:px-6 pb-4 md:py-6">
        <div class="hidden md:flex gap-4 xl:block xl:max-h-none justify-center">
          <div class="min-w-[336px] h-auto block sm:hidden xl:block xl:w-auto xl:h-auto sticky top-32">
            <card-store-ad
              v-if="storeBanner && storeBanner.image"
              light
              :product-image="storeBanner.image"
              :product-title="storeBanner.description"
              :product-link="storeBanner.link"
              :product-link-target="storeBanner.linkTarget"
              :button-text="storeBanner.buttonText"
              title-card="Tienda oficial"
            />
          </div>
        </div>
        <div
          v-if="seasonPlayers"
          :key="playersWrapperKey"
          class="xl:col-span-2 xl:auto-rows-min grid grid-cols-1 gap-2 bigsm:grid-cols-2 md:gap-4 relative"
        >
          <base-backdrop-loading :active="loadingSeasonData" />

          <card-no-data-message
            v-if="!seasonPlayers.length"
            :message="$t('shared.errorMessage.playerNoData')"
          />

          <div v-for="(player, i) in paginatedPlayers" :key="player.id">
            <card-player-featured-category
              :no-link="checkIfIsPLayerLegend(player.id)"
              :player-complete-name="player.shortName"
              :player-number="player.jersey"
              :player-position="player.role"
              :player-image="player.image"
              :season-id="seasonId"
              :player-id="player.id"
              :player="player"
              :mvp-partner="seasonMvpPartner"
              :player-team="player.teams ? player.teams[0] : undefined"
              :player-category="player.category"
              :loading-lazy="i > 3"
              with-team
              is-players-variable
            />
          </div>
          <card-no-data-message
            v-if="!filteredPlayers?.length && seasonPlayers.length"
            :message="$t('shared.filters.noData')"
          />
        </div>
      </div>
    </div>

    <pagination-nav
      v-model="currentPage"
      :total-items-amount="filteredPlayers?.length"
      :items-per-page="itemsPerPage"
    />

    <div class="block px-2 md:hidden w-full max-w-[336px] h-auto mx-auto py-6">
      <card-store-ad
        v-if="storeBanner && storeBanner.image"
        light
        :product-image="storeBanner.image"
        :product-title="storeBanner.description"
        :product-link="storeBanner.link"
        :product-link-target="storeBanner.linkTarget"
        :button-text="storeBanner.buttonText"
        title-card="Tienda oficial"
      />
    </div>
  </section>
</template>

<style scoped></style>
